import React, { useEffect, useCallback, useRef, useState } from "react";
import styled from "@emotion/styled/macro";
import { Outlet, useLocation, useMatch } from "react-router-dom";
import {
  Button,
  LoadingIndicator,
  tokens,
} from "@sunrun/experience-ui-components";
import { useMediaQuery } from "react-responsive";
import { useFlags } from "flagsmith/react";
import {
  CheckoutContentGrid,
  CheckoutFooterGrid,
  CheckoutGrid,
  CheckoutHeaderGrid,
  CheckoutSidebarGrid,
} from "../templates/CheckoutGrid";
import { CheckoutHeader } from "../molecules/CheckoutHeader";
import { TaskViewTemplate } from "../templates/TaskViewTemplate";
import { UpdateContactWarningBanner } from "../molecules/UpdateContactWarningBanner";
import { MaintenanceBanner } from "../molecules/MaintenanceBanner";
import { FeatureFlags } from "fixtures/features";
import { ContractReviewBanners } from "components/molecules/ContractReviewBanners";
import { TaskList } from "checkout/components/organisms/TaskList";
import { CheckoutFinalizeSplashModal } from "checkout/components/organisms/CheckoutFinalizeSplashModal";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { useCheckoutFooterContext } from "checkout/providers/CheckoutFooterContext";
import { CheckoutNavGuard } from "checkout/components/molecules/CheckoutNavGuard";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { Mixpanel } from "providers/mixpanel";
import { useAuthToken } from "hooks/useAuthToken";
import { CUSTOMER_SELF_CHECKOUT__CHECKOUT_PAGE_LOADED } from "constants/mixpanelEvents";
import { isMobileOrManufacturedHomeMessage } from "utils/contractReviewUtils";
import { HorizontalLine } from "components/atoms/HorizontalLine";

const CheckoutMainPage: React.FC = () => {
  const match = useMatch(
    "/prospect/:prospectId/offer/:offerId/checkout/:taskSlug"
  );

  const {
    offer,
    tasksLoading,
    completeCheckout,
    currentTask,
    currentTaskId,
    loadingOverride,
    titleOverride,
    showSecondaryWarningBanner,
    setShowSecondaryWarningBanner,
    showUtilityBillWarningBanner,
    setShowUtilityBillWarningBanner,
    showUtilityBillUploadRequirementBanner,
    prospectId,
    proposalId,
    contractReviewMessages,
    isNonSolarOffer,
    userProfile,
    isNewHomes,
  } = useCheckoutContext();
  const { footer } = useCheckoutFooterContext();
  const flags = useFlags([FeatureFlags.CHECKOUT_SRA]);
  const isCheckoutSraEnabled =
    flags[FeatureFlags.CHECKOUT_SRA]?.enabled ?? false;

  if (!match) {
    throw new Error("CheckoutMainPage must be rendered within matching route.");
  }
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;

  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const offerLabel = offer?.isNonSolar
    ? offer?.label
    : `${offer?.systemSize ?? "-"} kW | ${offer?.financial.financePlan ?? "-"}`;

  const location = useLocation();
  const hasTrackedRef = useRef(false);

  const trackCheckoutHasLoaded = useCallback(() => {
    const currentTabNameInPath = location?.pathname.split("/").pop();
    if (hasTrackedRef.current || !isCustomer) {
      return;
    }

    if (
      prospectId &&
      offer?.financial?.summary?.type &&
      proposalId &&
      currentTabNameInPath
    ) {
      Mixpanel.track(CUSTOMER_SELF_CHECKOUT__CHECKOUT_PAGE_LOADED, {
        prospectId,
        financePlan: offer?.financial?.summary?.type,
        proposalId,
        isCustomer,
        currentTabNameInPath,
      });
      hasTrackedRef.current = true;
    }
  }, [
    location?.pathname,
    isCustomer,
    prospectId,
    offer?.financial?.summary?.type,
    proposalId,
  ]);

  useEffect(() => {
    trackCheckoutHasLoaded();
  }, [trackCheckoutHasLoaded]);

  const showAdditionalSystemPill =
    currentTask?.id === "confirmNames" &&
    !isNonSolarOffer &&
    !isCheckoutSraEnabled;
  const showUtilityBillBanner =
    !isCheckoutSraEnabled && showUtilityBillUploadRequirementBanner;

  const shouldShowFinalizeSplash = completeCheckout && !tasksLoading;
  const [isSplashClosed, setIsSplashClosed] = useState(false);
  const isSplashOpen = shouldShowFinalizeSplash && !isSplashClosed;

  return (
    <CheckoutNavGuard>
      <CheckoutGrid isMobile={isMobile}>
        <CheckoutHeaderGrid>
          <CheckoutHeader
            headerTitle="Checkout"
            isMobile={isMobile}
            offerLabel={offerLabel}
            showUtilityBillUploadRequirementBanner={showUtilityBillBanner}
          />
          <MaintenanceBanner />
          {showSecondaryWarningBanner && (
            <UpdateContactWarningBanner
              contactType="secondary"
              onClose={() => setShowSecondaryWarningBanner(false)}
            />
          )}
          {showUtilityBillWarningBanner && (
            <UpdateContactWarningBanner
              contactType="utility_bill"
              onClose={() => setShowUtilityBillWarningBanner(false)}
            />
          )}
        </CheckoutHeaderGrid>
        <CheckoutSidebarGrid>
          {offer?.isNonSolar ? (
            <TaskList title={offer?.label} isMobile={isMobile} />
          ) : (
            <TaskList
              title={offerLabel}
              installationPartner={offer?.financial?.summary?.purchasedThru}
              isMobile={isMobile}
            />
          )}
        </CheckoutSidebarGrid>
        <CheckoutContentGrid>
          {isMobile && <HorizontalLine />}
          {currentTask ? (
            <>
              {!(currentTask.shouldHideHeaderOnLoading && loadingOverride) && (
                <ContractReviewBanners
                  messages={contractReviewMessages}
                  category="home_type_check"
                  filter={(message) =>
                    !isMobileOrManufacturedHomeMessage(message)
                  }
                  sessionDismissScopeId={proposalId}
                />
              )}
              <TaskViewTemplate
                showAdditionalSystemPill={showAdditionalSystemPill}
                loading={
                  currentTask.shouldHideHeaderOnLoading && loadingOverride
                }
                icon={currentTask.icon}
                title={
                  titleOverride?.[currentTaskId] ||
                  currentTask.taskViewTitle ||
                  currentTask.label
                }
              >
                <Outlet />
              </TaskViewTemplate>
            </>
          ) : (
            <LoadingWrapper>
              <LoadingIndicator color="black" />
            </LoadingWrapper>
          )}
        </CheckoutContentGrid>
        <CheckoutFooterGrid isMobile={isMobile}>
          {shouldShowFinalizeSplash ? (
            <FooterWithFinalizeSplashCta $isMobile={isMobile}>
              <FinalizeSplashButton
                color="secondary"
                size="md"
                $isMobile={isMobile}
                onClick={() => setIsSplashClosed(false)}
                data-testid="checkout-finalize-splash-cta"
              >
                Congrats & next steps
              </FinalizeSplashButton>
              {footer && <TaskFooter $isMobile={isMobile}>{footer}</TaskFooter>}
            </FooterWithFinalizeSplashCta>
          ) : (
            footer
          )}
        </CheckoutFooterGrid>
      </CheckoutGrid>
      <CheckoutFinalizeSplashModal
        open={isSplashOpen}
        prospectId={prospectId}
        userProfile={userProfile}
        isNewHomes={isNewHomes}
        onClose={() => setIsSplashClosed(true)}
      />
    </CheckoutNavGuard>
  );
};

const LoadingWrapper = styled.div`
  margin-top: 32px;
`;

const FooterWithFinalizeSplashCta = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? "column" : "row")};
  align-items: ${({ $isMobile }) => ($isMobile ? "stretch" : "center")};
  justify-content: flex-end;
  gap: ${({ $isMobile }) => ($isMobile ? "12px" : "24px")};
`;

const TaskFooter = styled.div<{ $isMobile: boolean }>`
  min-width: 0;
  flex: ${({ $isMobile }) => ($isMobile ? "none" : "0 1 auto")};
`;

const FinalizeSplashButton = styled(Button)<{ $isMobile: boolean }>`
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "fit-content")};
  flex-shrink: 0;
  padding: 0 32px;
  border: 1px solid ${tokens.BRAND_HERO_BLUE};
  border-radius: 80px;
  color: ${tokens.BRAND_HERO_BLUE};
`;

export { CheckoutMainPage };
